/**
 * The zoomed view of the picture: `k` times larger, moved by `tx`, `ty` (CSS pixels of the frame), so a point of
 * the plain picture at p shows at p * k + t. Pure: the player keeps one of these and applies it to the video
 * element (a CSS transform) and to the canvas mapping alike, so dots, boxes, the line and every click stay in step.
 * `rect` is where the plain picture sits in the frame ({ x, y, w, h }, letterboxing included).
 */

export const PLAIN = Object.freeze({ k: 1, tx: 0, ty: 0 });

// how close two pointer positions can be and still count as a click rather than a drag (CSS pixels)
export const DRAG_PX = 4;

/** The most the picture zooms: about twice its own pixels, past which there is nothing more to see; a small video
 * still gets 2×, nothing goes over 8×. */
export const zoomMax = (nativeWidth, shownWidth) => {
	if (!(nativeWidth > 0) || !(shownWidth > 0)) return 2;
	return Math.min(8, Math.max(2, (2 * nativeWidth) / shownWidth));
};

/** The zoomed picture never leaves a gap inside the plain picture's area; not zoomed is always PLAIN. */
export const clampView = ({ k, tx, ty }, rect) => {
	if (!(k > 1)) return PLAIN;
	const minX = (rect.x + rect.w) * (1 - k);
	const maxX = rect.x * (1 - k);
	const minY = (rect.y + rect.h) * (1 - k);
	const maxY = rect.y * (1 - k);
	return {
		k,
		tx: Math.min(maxX, Math.max(minX, tx)),
		ty: Math.min(maxY, Math.max(minY, ty)),
	};
};

/** Zoom by `factor`, keeping the picture point under the cursor (frame CSS pixels) where it is. The same view
 * comes back when nothing can change (already plain and zooming out, or at the most). */
export const zoomAt = (view, rect, [cx, cy], factor, kMax) => {
	const k = Math.min(Math.max(1, kMax), Math.max(1, view.k * factor));
	if (k === view.k) return view;
	const ux = (cx - view.tx) / view.k;
	const uy = (cy - view.ty) / view.k;
	return clampView({ k, tx: cx - ux * k, ty: cy - uy * k }, rect);
};

/** The view moved by a drag of (dx, dy) CSS pixels from `view`. */
export const panBy = (view, rect, dx, dy) =>
	clampView({ k: view.k, tx: view.tx + dx, ty: view.ty + dy }, rect);

/** Where the plain picture's `rect` is once the view applies. */
export const zoomedRect = (rect, { k, tx, ty }) => ({
	x: rect.x * k + tx,
	y: rect.y * k + ty,
	w: rect.w * k,
	h: rect.h * k,
});

/** A wheel event as a zoom factor: up zooms in. A mouse notch (deltaY 100) is about 13 %, a trackpad sends many
 * small steps; line and page deltas are scaled to pixels first. */
export const wheelFactor = ({ deltaY, deltaMode }) => {
	const px = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
	return Math.exp(-px * 0.0012);
};

/** The CSS transform that shows `view`, or none when plain. */
export const viewTransform = ({ k, tx, ty }) =>
	k === 1 ? '' : `translate(${tx}px, ${ty}px) scale(${k})`;
